<script lang="ts" setup>
	import { Float } from '@headlessui-float/vue'
	import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
	import {
		EllipsisVerticalIcon,
		PencilSquareIcon,
	} from '@heroicons/vue/20/solid'

	import HasPermission from '@/components/HasPermission.vue'

	import useStatusHotel from '@/internal/modules/providers/composables/useStatusHotel'
	import { Hotel } from '@/internal/modules/providers/interfaces/Hotel'

	import ModalUpdateCodeAmadeusCode from './ModalUpdateCodeAmadeusCode.vue'

	interface Props {
		hotel: Hotel
	}

	interface Emits {
		(e: 'setHotelIdComments', id: number): void
		(e: 'setHotelIdChangeState', id: string, stateId: number): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()

	const { data: status } = useStatusHotel({
		immediate: true,
	})

	const stateIcon = (name: string) => {
		switch (name) {
			case 'Activo':
				return 'check'
			case 'Inactivo':
				return 'remove_done'
			case 'Lista Negra':
				return 'delete'
		}
	}

	const stateClass = (name: string) => {
		switch (name) {
			case 'Activo':
				return 'bg-green-500'
			case 'Inactivo':
				return 'bg-red-500'
			case 'Lista Negra':
				return 'bg-black'
		}
	}
</script>

<template>
	<div
		class="border rounded-lg border-gray-300 bg-white overflow-hidden shadow-sm mb-2"
	>
		<div class="p-4">
			<div class="flex items-start justify-between">
				<div>
					<h3 class="font-medium capitalize">{{ hotel.name }}</h3>
					<p
						v-if="hotel.amadeusCode"
						class="text-xs text-gray-500"
					>
						Código: {{ hotel.amadeusCode }}
					</p>
					<ModalUpdateCodeAmadeusCode
						:hotel="hotel"
						v-else
					/>
				</div>
				<div class="flex items-center gap-2">
					<!-- Status Badge -->
					<p
						:class="stateClass(hotel.stateName)"
						class="flex items-center gap-1 text-xs rounded-full text-white p-1"
					>
						<span
							:class="[
								'text-sm rounded-full flex items-center gap-1 material-symbols-outlined',
							]"
						>
							{{ stateIcon(hotel.stateName) }}
						</span>
						{{ hotel.stateName }}
					</p>

					<!-- Options Dropdown -->
					<Menu
						as="div"
						class="relative inline-block text-left"
					>
						<Float
							placement="bottom-end"
							:offset="4"
							strategy="fixed"
							flip
						>
							<MenuButton
								class="flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-200"
							>
								<EllipsisVerticalIcon
									class="h-5 w-5 text-gray-600"
									aria-hidden="true"
								/>
							</MenuButton>
							<MenuItems
								class="w-56 divide-y divide-gray-100 rounded-xl shadow-xl ring-1 bg-white ring-black ring-opacity-5 focus:outline-none"
							>
								<div class="py-1">
									<HasPermission
										:name-permission="'viewAny-internal'"
										:entity-type="'App\\Models\\Hotel'"
									>
										<MenuItem v-slot="{ active }">
											<RouterLink
												:to="{
													name: 'hotels.hotel.edit',
													params: { id: hotel.id },
												}"
												:class="[
													active ? 'bg-gray-50 text-blue-600' : 'text-gray-700',
													'group flex items-center px-4 py-2 text-sm transition-colors duration-150',
												]"
											>
												<PencilSquareIcon
													class="mr-3 h-5 w-5 text-gray-400 group-hover:text-blue-500"
													aria-hidden="true"
												/>
												Editar
											</RouterLink>
										</MenuItem>
									</HasPermission>

									<HasPermission
										:entity-type="'App\\Models\\Hotel'"
										:name-permission="'view-comments-internal'"
									>
										<MenuItem v-slot="{ active, close }">
											<button
												class="w-full"
												:class="[
													active ?
														'bg-gray-100 text-gray-900'
													:	'text-gray-700',
													'group flex items-center px-4 py-2 text-sm gap-2',
												]"
												@click="
													() => {
														emits('setHotelIdComments', hotel.id)
														close()
													}
												"
											>
												<small class="material-symbols-outlined text-gray-400"
													>history</small
												>
												Historial de comentarios
											</button>
										</MenuItem>
									</HasPermission>

									<HasPermission
										:name-permission="'change-status-internal'"
										:entity-type="'App\\Models\\Hotel'"
									>
										<div
											v-for="state in status"
											:key="state.id"
										>
											<MenuItem
												as="div"
												v-slot="{ close }"
											>
												<button
													v-if="state.id !== hotel.stateId"
													class="w-full flex items-center px-4 py-2 text-sm gap-2 hover:bg-gray-100"
													:class="'hover:bg-gray-100 text-gray-700'"
													@click="
														() => {
															emits(
																'setHotelIdChangeState',
																hotel.id.toString(),
																state.id
															)
															close()
														}
													"
												>
													<small
														class="material-symbols-outlined text-gray-400"
														>{{ stateIcon(state.displayName) }}</small
													>
													{{ state.displayName }}
												</button></MenuItem
											>
										</div>
									</HasPermission>
								</div>
							</MenuItems>
						</Float>
					</Menu>
				</div>
			</div>

			<!-- Hotel Details -->
			<div class="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-sm pt-2">
				<div>
					<span class="text-gray-500 text-xs">Cadena:</span>
					{{ hotel.chainName }}
				</div>
				<div>
					<span class="text-gray-500 text-xs">Marca:</span>
					{{ hotel.brandName }}
				</div>
				<div class="col-span-2">
					<span class="text-gray-500 text-xs">Grupo:</span>
					{{ hotel.groupName }}
				</div>
			</div>
		</div>
	</div>
</template>
